import { ArrowLeft, CheckCircle2, Clock, Sparkles, Music, Users, Trophy, ListMusic, MessageSquare, Zap, Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import Navigation from '@/components/Navigation';
import { Link } from 'react-router-dom';

const Features = () => {
  const navigate = useNavigate();

  const currentFeatures = [
    {
      icon: Music,
      title: 'Music Leagues',
      description: 'Create and join music leagues with friends. Compete by submitting songs based on themes.',
      status: 'live',
    },
    {
      icon: Users,
      title: 'Social Voting',
      description: 'Vote on submissions and see real-time results. Comment and react to songs.',
      status: 'live',
    },
    {
      icon: Trophy,
      title: 'Leaderboards',
      description: 'Track your progress and see who has the best music taste in your league.',
      status: 'live',
    },
    {
      icon: ListMusic,
      title: 'Playlist Archive',
      description: 'Browse and play through historical playlists from past seasons.',
      status: 'live',
    },
    {
      icon: MessageSquare,
      title: 'Comments & Reactions',
      description: 'Leave comments and reactions on submissions. See what others think about songs.',
      status: 'live',
    },
    {
      icon: Zap,
      title: 'Fun Facts',
      description: 'AI-generated fun facts about songs to enhance your listening experience.',
      status: 'live',
    },
    {
      icon: Shield,
      title: 'Admin Panel',
      description: 'Manage leagues, rounds, and submissions with comprehensive admin tools.',
      status: 'live',
    },
  ];

  const upcomingFeatures = [
    {
      icon: Music,
      title: 'Spotify Integration',
      description: 'Deep integration with Spotify for seamless song discovery and playback.',
      status: 'planned',
      eta: 'Q2 2025',
    },
    {
      icon: Users,
      title: 'Public Leagues',
      description: 'Join public leagues and compete with music lovers from around the world.',
      status: 'planned',
      eta: 'Q2 2025',
    },
    {
      icon: Trophy,
      title: 'Achievements & Badges',
      description: 'Unlock achievements and earn badges for your music discovery milestones.',
      status: 'planned',
      eta: 'Q3 2025',
    },
    {
      icon: MessageSquare,
      title: 'Direct Messaging',
      description: 'Chat with league members and discuss submissions in real-time.',
      status: 'planned',
      eta: 'Q3 2025',
    },
    {
      icon: Zap,
      title: 'Mobile App',
      description: 'Native iOS and Android apps for on-the-go music league participation.',
      status: 'planned',
      eta: 'Q4 2025',
    },
    {
      icon: ListMusic,
      title: 'Custom Playlists',
      description: 'Create and share custom playlists from your favorite submissions.',
      status: 'planned',
      eta: 'Q4 2025',
    },
    {
      icon: Music,
      title: 'Music Recommendations',
      description: 'Get personalized music recommendations based on your voting history.',
      status: 'planned',
      eta: 'Q1 2026',
    },
    {
      icon: Users,
      title: 'Team Leagues',
      description: 'Form teams and compete in team-based music leagues.',
      status: 'planned',
      eta: 'Q1 2026',
    },
  ];

  const FeatureCard = ({ feature, isUpcoming = false }: { feature: typeof currentFeatures[0] | typeof upcomingFeatures[0]; isUpcoming?: boolean }) => {
    const Icon = feature.icon;
    return (
      <Card className="h-full hover:shadow-lg transition-shadow">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Icon className="h-5 w-5 text-primary" />
              </div>
              <CardTitle className="text-lg">{feature.title}</CardTitle>
            </div>
            {isUpcoming && 'eta' in feature && (
              <Badge variant="outline" className="text-xs">
                {feature.eta}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-sm">{feature.description}</p>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">Features & Roadmap</h1>
          <p className="text-muted-foreground text-lg">
            See what's available now and what's coming next to BeatBattle.
          </p>
        </div>

        {/* Current Features */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <CheckCircle2 className="h-6 w-6 text-green-500" />
            <h2 className="text-2xl font-semibold">Current Features</h2>
            <Badge variant="secondary" className="ml-2">Live</Badge>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {currentFeatures.map((feature, index) => (
              <FeatureCard key={index} feature={feature} />
            ))}
          </div>
        </div>

        {/* Upcoming Features */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <Clock className="h-6 w-6 text-yellow-500" />
            <h2 className="text-2xl font-semibold">Upcoming Features</h2>
            <Badge variant="outline" className="ml-2">In Development</Badge>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {upcomingFeatures.map((feature, index) => (
              <FeatureCard key={index} feature={feature} isUpcoming />
            ))}
          </div>
        </div>

        {/* Feedback CTA */}
        <Card className="bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <Sparkles className="h-6 w-6 text-primary" />
                <div>
                  <h3 className="font-semibold text-lg">Have a Feature Request?</h3>
                  <p className="text-muted-foreground text-sm">
                    Share your ideas and help shape the future of BeatBattle.
                  </p>
                </div>
              </div>
              <Button asChild className="btn-music">
                <Link to="/feedback">
                  Submit Feedback
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Features;

